import { getTranslations } from "contexts/LocalizationContext/utils";
import type { GetServerSideProps, NextPage } from "next";
import getHelpCategory from "utils/serverSideUtils/getHelpCategory";
import type { SerializedHelpQuestion } from "utils/serverSideUtils/getHelpQuestionsByCategory";
import getHelpQuestionsByCategory from "utils/serverSideUtils/getHelpQuestionsByCategory";
import getServerSideStoryblokPages from "utils/serverSideUtils/getServerSideStoryblokPages";

import { DataContextProvider } from "contexts/DataContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { SideMenuContextProvider } from "contexts/SideMenuContext";
import Footer from "modules/common/components/Footer";
import Grid from "modules/common/components/Grid";
import { MenuItems, HeaderNav, CheckCompensationButton } from "modules/common/components/HeaderNav";
import { PressLogos } from "modules/common/components/PressLogos";
import { DynamicComponent } from "modules/storyblok/DynamicComponent";
import CustomHead from "modules/common/components/CustomHead";
import { useLanguage } from "contexts/LocalizationContext";
import { useTopMenu } from "utils/menuUtils";
import { SideMenu, SideMenuMobileAdapter } from "components";
import { checkIfValidLanguage } from "utils/localeUtils";
import type { StoryData } from "storyblok-js-client";
import type { CmsLanguagesDataProps } from "modules/magnitude/templates/MagnitudeTemplate";
import { StickyBottomBarContextProvider, useStickyBottomBarContext } from "contexts/StickyBottomBarContext";
import StickyBottomBarAdapter from "modules/storyblok/adapters/StickyBottomBar";
import { ChakraHeading, ChakraGridItem as GridItem, ChakraBox as Box } from "@airhelp/react-ui-components";
import type { LanguageCode } from "config/languages";

interface HelpCategoryPage {
  helpCategory: StoryData;
  questions: SerializedHelpQuestion[];
  serverSideStoryblokPages: Record<number, Record<string, number>>;
  cmsLanguagesData: CmsLanguagesDataProps[];
}

const HelpCategoryPage = ({ helpCategory, questions }: Pick<HelpCategoryPage, "helpCategory" | "questions">) => {
  const {
    name: categoryName,
    tag_list: tagList,
    content: { metaTitle, metaDescription, hideStickyBottomBar, stickyBottomBarVariant, stickyBottomBarHeading },
  } = helpCategory;

  const noindex = tagList.includes("noindex");
  const language = useLanguage();
  const topMenu = useTopMenu(language);
  const { setFirstTriggerElement } = useStickyBottomBarContext();

  return (
    <>
      <CustomHead title={metaTitle} description={metaDescription} noindex={noindex} />
      <HeaderNav
        menuItems={<MenuItems menu={topMenu} />}
        headerButtons={<CheckCompensationButton />}
        subMenu={<SideMenuMobileAdapter pageHeading={categoryName} />}
      />
      <Grid mb={{ base: 20, md: 32 }} pt={{ base: 20, md: 32 }}>
        <GridItem gridColumn={{ base: "initial", md: "span 3" }}>
          <Box position="sticky" top={128} display={{ base: "none", md: "block" }}>
            <SideMenu pageHeading={categoryName} />
          </Box>
        </GridItem>
        <GridItem gridColumn={{ base: "initial", md: "span 8" }}>
          <ChakraHeading variant="h1" as="h1">
            {categoryName}
          </ChakraHeading>
          <div ref={setFirstTriggerElement} />
          {questions
            ? questions.map((question) => (
                <Box key={question.content._uid} _notLast={{ mb: 10 }}>
                  <DynamicComponent blok={question.content} />
                </Box>
              ))
            : null}
        </GridItem>
      </Grid>
      <PressLogos />
      <Footer />
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </>
  );
};

const PageWithProviders: NextPage<HelpCategoryPage> = ({
  helpCategory,
  questions,
  serverSideStoryblokPages,
  cmsLanguagesData,
}: HelpCategoryPage) => (
  <DataContextProvider data={{ serverSideStoryblokPages, cmsLanguagesData }}>
    <StickyBottomBarContextProvider threshold={0.4}>
      <MobileMenuContextProvider>
        <ModalContextProvider>
          <SideMenuContextProvider>
            <HelpCategoryPage helpCategory={helpCategory} questions={questions} />
          </SideMenuContextProvider>
        </ModalContextProvider>
      </MobileMenuContextProvider>
    </StickyBottomBarContextProvider>
  </DataContextProvider>
);

export default PageWithProviders;

export const getServerSideProps: GetServerSideProps = async ({ query: { language, category } }) => {
  if (!checkIfValidLanguage(language as LanguageCode)) {
    return {
      notFound: true,
    };
  }

  const [translations, helpCategory, serverSideStoryblokPages] = await Promise.all([
    getTranslations(language as LanguageCode, []),
    getHelpCategory(language as LanguageCode, category[0]),
    getServerSideStoryblokPages(),
  ]);

  if (!helpCategory) {
    return {
      notFound: true,
    };
  }

  const questions = await getHelpQuestionsByCategory(language as LanguageCode, helpCategory.uuid);

  const cmsLanguagesData = [...helpCategory.alternates, { full_slug: helpCategory.full_slug }];

  return {
    props: {
      translations,
      language,
      serverSideStoryblokPages,
      cmsLanguagesData,
      helpCategory,
      questions,
    },
  };
};
